import { XIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { usePlanOptions, useStartCheckout } from '@/api/hooks/useBilling'
import { StateSwitcher } from '@/components/atoms/state-switcher'
import { AddSeatSummary, type AddSeatPreview } from '@/components/molecules/add-seat-summary'
import { PlanRow } from '@/components/molecules/plan-row'
import { SubscribeBenefits } from '@/components/molecules/subscribe-benefits'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { useIndividuals } from '@/hooks/useIndividuals'
import { formatAmount } from '@/lib/money'
import { cn } from '@/lib/utils'

type PlanOption = {
  additionalAmount: number
  /**
   * What this owner pays for the individual being subscribed, which is not always the
   * list price — someone who already holds a seat is buying an additional one. Always
   * display this rather than `firstAmount`, or the dialog quotes a figure that checkout
   * will not charge.
   */
  amount: number
  currency: string
  firstAmount: number
  interval: 'month' | 'year'
  recurring: boolean
}

const optionKey = (option: Pick<PlanOption, 'interval' | 'recurring'>) =>
  `${option.interval}-${option.recurring ? 'recurring' : 'once'}`

/**
 * Picks a plan and hands off to Stripe Checkout.
 *
 * Split in two: the choice on the left, what it buys on the right. The period (monthly /
 * annual) is its own control so the annual price reads as a comparison rather than a
 * second, separate decision. Buying outright is offered underneath, but only on the annual
 * tab — it is a year that is sold that way, so showing it under "monthly" would name one
 * period and charge for another.
 *
 * Amounts are fetched rather than written down. A Stripe Price cannot be repriced — a new
 * one is created instead — so any number hardcoded here would quietly become wrong the
 * day the second-individual discount moves, which it is expected to.
 *
 * Only ever offers the currently selected individual. Paying for several at once is what
 * the quantity on the subscription is for, and that is managed in Stripe's own portal
 * rather than rebuilt here.
 *
 * An owner who already subscribes sees something else entirely. They are not choosing a
 * plan — the individual joins the subscription they have, which is the only way the
 * reduced price for a further individual is ever reached — so the picker is replaced by
 * what that costs. It also doubles as the confirmation: there is no Stripe page on that
 * path, the card on file is charged, so the amount has to be stated before the button.
 */
export const SubscribeDialog = ({
  onOpenChange,
  open,
}: {
  onOpenChange(open: boolean): void
  open: boolean
}) => {
  const { i18n, t } = useTranslation()
  const { selectedIndividual, selectedIndividualId } = useIndividuals()
  const { data, isError, isLoading } = usePlanOptions()
  const checkout = useStartCheckout()

  const plans = useMemo(() => (data?.options ?? []) as PlanOption[], [data])
  const addSeat = (data?.addSeat ?? null) as AddSeatPreview | null
  const monthRecurring = plans.find(plan => plan.interval === 'month' && plan.recurring)
  const yearRecurring = plans.find(plan => plan.interval === 'year' && plan.recurring)
  const yearOnce = plans.find(plan => plan.interval === 'year' && !plan.recurring)

  // Monthly is the default. It is the smaller commitment, and the annual tab makes its own
  // case from there — the savings badge only reads as a saving next to something.
  const [period, setPeriod] = useState<'month' | 'year'>('month')
  const topPlan = period === 'year' ? yearRecurring : monthRecurring
  // Must agree with `period`: the effect below only re-selects when something changes, so a
  // mismatched initial pair would survive until the user touched a tab.
  const [selected, setSelected] = useState<string>('month-recurring')

  // Switching the period tab moves the highlighted card along with it — unless the user
  // had explicitly picked buying outright and is still on the tab that offers it. Leaving
  // that selection in place after a switch to monthly would hold a choice the user can no
  // longer see, and send them to a yearly checkout from a tab reading "Månadsvis".
  useEffect(() => {
    if (yearOnce && period === 'year' && selected === optionKey(yearOnce)) return
    if (topPlan) setSelected(optionKey(topPlan))
  }, [period, topPlan, yearOnce])

  const chosen = plans.find(plan => optionKey(plan) === selected)

  const renewal = addSeat?.renewsAt
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(
        new Date(addSeat.renewsAt),
      )
    : undefined

  /**
   * What a plan saves against paying monthly, as a badge.
   *
   * Per plan rather than once for the annual subscription. Buying a year outright costs
   * the same as subscribing to one, so it saves the same 20% — and a badge on only one of
   * two identically priced rows reads as the other one not being a saving.
   *
   * Always measured against the monthly recurring price, because that is the alternative
   * being chosen between. A monthly plan measured against itself is zero and renders
   * nothing, which is why there is no separate check for the period.
   */
  const savingsBadge = (plan?: PlanOption) => {
    if (!plan || !monthRecurring || plan.interval !== 'year') return null

    // Measured on what is actually shown, so a reader who divides the annual figure by
    // twelve and compares it with the monthly one arrives at the same percentage.
    const percent = Math.round((1 - plan.amount / 12 / monthRecurring.amount) * 100)

    if (percent <= 0) return null

    return (
      <Badge className="border-transparent bg-brand-lime text-brand-gran">
        {t('subscribeDialog.savings', { percent })}
      </Badge>
    )
  }

  const onConfirm = async () => {
    if (!selectedIndividualId) return

    // The seat addition is not one of the plans — there is nothing to pick, so it carries
    // its own terms. The backend reaches the same conclusion independently; this only
    // has to agree with it.
    const terms = addSeat
      ? { interval: addSeat.interval, recurring: true }
      : chosen && { interval: chosen.interval, recurring: chosen.recurring }

    if (!terms) return

    // No dialog close on success — `start` redirects this tab, and closing first would
    // flash the page underneath on the way out.
    await checkout.start({ individualIds: [selectedIndividualId], ...terms })
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className="inset-0 max-w-[900px] translate-x-0 translate-y-0 gap-0 overflow-y-auto border-0 p-0 sm:inset-auto sm:left-[50%] sm:top-[50%] sm:h-auto sm:-translate-x-1/2 sm:-translate-y-1/2 sm:overflow-hidden"
        hideCloseButton
      >
        {/* Outlined, like Cancel and most of our other buttons on white — a plain ghost
            icon disappears against the white form section mobile stacks on top. On
            desktop this corner sits over the dark green column instead, where the
            outline would be the odd one out, so it drops back to a plain white icon. */}
        <Button
          className="absolute right-4 top-4 hover:border-brand-lime hover:bg-brand-lime hover:text-brand-gran sm:border-transparent sm:bg-transparent sm:text-white"
          onClick={() => onOpenChange(false)}
          size="icon"
          variant="outline"
        >
          <XIcon />
          <span className="sr-only">{t('global.close')}</span>
        </Button>

        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-[22px] bg-white p-8">
            <div>
              <DialogTitle className="font-heading text-[26px] font-extrabold tracking-tight">
                {t(addSeat ? 'subscribeDialog.addSeat.title' : 'subscribeDialog.title', {
                  name: selectedIndividual?.firstName ?? '',
                })}
              </DialogTitle>
              <DialogDescription className="mt-2 text-[15px]">
                {t(addSeat ? 'subscribeDialog.addSeat.subtitle' : 'subscribeDialog.subtitle', {
                  name: selectedIndividual?.firstName ?? '',
                })}
              </DialogDescription>
            </div>

            {!addSeat && (
              <div className="flex gap-1.5 rounded-lg bg-layer-2 p-1">
                {(['month', 'year'] as const).map(value => (
                  <button
                    className={cn(
                      'flex-1 rounded-md px-3.5 py-2.5 text-sm font-bold transition-colors',
                      period === value ? 'bg-layer-1 text-brand-gran shadow-sm' : 'text-[#586574]',
                    )}
                    key={value}
                    onClick={() => setPeriod(value)}
                    type="button"
                  >
                    {t(
                      value === 'year'
                        ? 'subscribeDialog.period.annual'
                        : 'subscribeDialog.period.monthly',
                    )}
                  </button>
                ))}
              </div>
            )}

            <StateSwitcher isError={isError} isLoading={isLoading}>
              {addSeat ? (
                <AddSeatSummary addSeat={addSeat} locale={i18n.language} renewal={renewal} />
              ) : (
                // Fixed height reserved for the tallest state (Annual: two rows plus the
                // per-additional-individual note), so switching to Monthly — fewer rows, and
                // that note doesn't always apply — leaves blank space below instead of
                // shrinking the dialog under the cursor.
                <div className="flex min-h-[248px] flex-col gap-3">
                  {topPlan && (
                    <PlanRow
                      badge={savingsBadge(topPlan)}
                      caption={t('subscribeDialog.plan.renewsAutomatically')}
                      isSelected={selected === optionKey(topPlan)}
                      onClick={() => setSelected(optionKey(topPlan))}
                      price={formatAmount(topPlan.amount, topPlan.currency, i18n.language)}
                      priceUnit={t(
                        period === 'year'
                          ? 'subscribeDialog.plan.perYear'
                          : 'subscribeDialog.plan.perMonth',
                      )}
                      title={t(
                        period === 'year'
                          ? 'subscribeDialog.plan.year'
                          : 'subscribeDialog.plan.month',
                      )}
                    />
                  )}
                  {period === 'year' && yearOnce && (
                    <PlanRow
                      badge={savingsBadge(yearOnce)}
                      caption={t('subscribeDialog.plan.onceCaption')}
                      isSelected={selected === optionKey(yearOnce)}
                      onClick={() => setSelected(optionKey(yearOnce))}
                      price={formatAmount(yearOnce.amount, yearOnce.currency, i18n.language)}
                      priceUnit={t('subscribeDialog.plan.once')}
                      title={t('subscribeDialog.plan.onceTitle')}
                    />
                  )}
                  {/* Only while they are still paying the full price. Once `amount` has
                      already dropped to the additional one, repeating it reads as a
                      further reduction rather than as the one being applied. */}
                  {chosen &&
                    chosen.amount === chosen.firstAmount &&
                    chosen.additionalAmount !== chosen.firstAmount && (
                      <p className="text-xs text-[#586574]">
                        {t('subscribeDialog.additionalIndividual', {
                          amount: formatAmount(
                            chosen.additionalAmount,
                            chosen.currency,
                            i18n.language,
                          ),
                        })}
                      </p>
                    )}
                  <p className="mt-auto text-xs text-[#586574]">{t('subscribeDialog.taxNote')}</p>
                </div>
              )}
            </StateSwitcher>

            <DialogFooter className="mt-auto flex-col gap-2.5 sm:flex-row sm:space-x-0">
              <Button
                className="flex-1"
                onClick={() => onOpenChange(false)}
                type="button"
                variant="outline"
              >
                {t('global.cancel')}
              </Button>
              <Button
                className="flex-[2]"
                disabled={(!chosen && !addSeat) || !selectedIndividualId}
                isLoading={checkout.isPending}
                onClick={onConfirm}
              >
                {t(addSeat ? 'subscribeDialog.addSeat.confirm' : 'subscribeDialog.confirm')}
              </Button>
            </DialogFooter>
          </div>

          <SubscribeBenefits />
        </div>
      </DialogContent>
    </Dialog>
  )
}
